import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import Sidebar from "@/components/Sidebar";
import Dashboard from "@/components/Dashboard";
import ImportPage from "@/components/ImportPage";
import PlayerDetail from "@/components/PlayerDetail";
import NegativeOverview from "@/components/NegativeOverview";
import TeamComparison from "@/components/TeamComparison";
import MVPPage from "@/components/MVPPage";
import RaidCalendar from "@/components/RaidCalendar";
import RaidDetail from "@/components/RaidDetail";
import RaidComparison from "@/components/RaidComparison";
import WeeklyReport from "@/components/WeeklyReport";
import UpgradeFlags from "@/components/UpgradeFlags";
import BossHistory from "@/components/BossHistory";
import LoginPage from "@/components/LoginPage";
import GuildPicker from "@/components/GuildPicker";
import { getPlayers, getRaids, getStats } from "@/lib/api";

function AppContent() {
  const { user, loading: authLoading, activeGuild } = useAuth();
  const [team, setTeam] = useState("");

  // Whenever the active guild changes, snap the team selector to the first team
  // of that guild (only if the current team isn't already in the list).
  useEffect(() => {
    if (activeGuild?.teams?.length) {
      if (!team || !activeGuild.teams.includes(team)) {
        setTeam(activeGuild.teams[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeGuild?.id, activeGuild?.teams]);

  const [players, setPlayers] = useState([]);
  const [raids, setRaids] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const loadData = useCallback(async () => {
    if (!user || !activeGuild || !team) { setLoading(false); return; }
    setLoading(true);
    try {
      const [p, r, s] = await Promise.all([
        getPlayers(team), getRaids(team), getStats()
      ]);
      setPlayers(p);
      setRaids(r);
      setStats(s);
    } catch (e) {
      console.error("Failed to load data:", e);
    }
    setLoading(false);
  }, [team, user, activeGuild]);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── Auth gate: hide entire app shell when not logged in ───
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground" data-testid="auth-loading">
        Loading…
      </div>
    );
  }
  if (!user) {
    // No sidebar, no main content — only the login screen
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }
  // Gate: logged-in but no guild → show GuildPicker
  if (user && !activeGuild) {
    return <GuildPicker />;
  }

  return (
    <div className="app-layout">
      <Sidebar
        team={team}
        setTeam={setTeam}
        players={players}
        onNavigate={navigate}
        currentPath={location.pathname}
        onDataChanged={loadData}
      />
      <main className="main-content">
        <Routes>
          <Route path="/" element={
            <Dashboard stats={stats} raids={raids} team={team} onNavigate={navigate} onDataChanged={loadData} />
          } />
          <Route path="/login" element={
            <LoginPage />
          } />
          <Route path="/import" element={
            <ImportPage team={team} onImported={loadData} onNavigate={navigate} />
          } />
          <Route path="/player/:playerId" element={
            <PlayerDetailWrapper raids={raids} team={team} players={players} onDataChanged={loadData} />
          } />
          <Route path="/negative-overview" element={
            <NegativeOverview raids={raids} team={team} />
          } />
          <Route path="/team-battle" element={
            <TeamComparison />
          } />
          <Route path="/mvp" element={
            <MVPPage />
          } />
          <Route path="/calendar" element={
            <RaidCalendar onNavigate={navigate} />
          } />
          <Route path="/raid/:raidId" element={
            <RaidDetail />
          } />
          <Route path="/raid-comparison" element={
            <RaidComparison />
          } />
          <Route path="/weekly-report" element={
            <WeeklyReport />
          } />
          <Route path="/upgrade-flags" element={
            <UpgradeFlags />
          } />
          <Route path="/boss-history" element={
            <BossHistory />
          } />
        </Routes>
      </main>
    </div>
  );
}

function PlayerDetailWrapper({ raids, team, players, onDataChanged }) {
  const { playerId } = useParams();
  return <PlayerDetail playerId={playerId} raids={raids} team={team} allPlayers={players} onDataChanged={onDataChanged} />;
}

export default function App() {
  return (
    <TooltipProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppContent />
          <Toaster position="bottom-right" theme="dark" />
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  );
}
